/**
 * View-aware notification navigation.
 *
 * The app has three user-facing VIEWS (User / Manager / Admin — what the
 * ViewTypeSwitcher toggles) served by different shells depending on the
 * account's role:
 *
 *   role admin:    Admin View  → /dashboard/*            User View → /userprofileadmin/*
 *   role manager:  Manager (or Admin) View → /DashboardManager/*   User View → /userprofilemanager/*
 *   role user:     User View  → /userprofile/* (UserRoutes)
 *                  Admin View → /userprofile/* (AdminRoutes, admin-level users only)
 *   superadmin:    /DashboardSuper/* (no view switching)
 *
 * A notification's destination page may live in a DIFFERENT view than the
 * one currently active. This module resolves the owning view for the viewer,
 * validates the page actually exists in that view's shell, and — via
 * applyViewSwitch — synchronizes the view state (localStorage viewType /
 * managerViewType + the managerViewTypeChanged event every switcher and
 * dual-mode shell listens to) BEFORE navigation happens.
 */
import { getNotificationTarget } from '../constant/notificationRoutes';
import pageToModuleMap from '../pages/Admin_Acount/config/pageToModuleMap';

// ── Route segments each shell actually defines ──────────────────────────────
// (Keep in sync with the <Routes> blocks of the respective shells.)

// pages/Admin_Acount/Admin_Acount.jsx (/dashboard/*)
const ADMIN_SHELL_PAGES = [
  'dashboard', 'employees', 'payroll', 'attendance', 'analytics', 'reports',
  'calendar', 'configuration', 'adminLevels', 'departments', 'messages',
  'EscalationGroupMapping', 'Mo3tiAdminDocumentRequests',
  'Mo3tiAdvancementManagement', 'TaxCalculator', 'LeaveApprovalPage',
  'ChangesPage', 'Settings',
];

// pages/Manager_Account/index.jsx (/DashboardManager/*)
const MANAGER_SHELL_PAGES = [
  'dashboard', 'employees', 'attendance', 'analytics', 'reports', 'calendar',
  'messages', 'Mo3tiAdminDocumentRequests', 'Mo3tiAdvancementManagement',
  'Payroll', 'TaxCalculator', 'LeaveApprovalPage', 'ChangesPage',
  'companyConfig', 'Settings',
];

// The three User View shells (UserRoutes.jsx, (Admin) Profile/UserProfile.jsx,
// (Manager)user-profile/Mo3tiUserProfile.jsx) all define the same segments.
const USER_VIEW_PAGES = [
  'professionalProfile', 'attendance', 'salary', 'employee-services',
  'calendar', 'messages', 'leave-management', 'escalation', 'groups',
  'settings',
];

// pages/User_Account/routes/AdminRoutes.jsx (/userprofile/* in admin viewMode)
const USER_ADMIN_ROUTES_PAGES = [
  'dashboard', 'employees', 'attendance', 'reports', 'calendar', 'messages',
  'Mo3tiAdminDocumentRequests', 'LeaveApprovalPage',
  'Mo3tiAdvancementManagement', 'ChangesPage', 'Payroll', 'TaxCalculator',
  'EscalationGroupMapping', 'groups', 'Settings',
];

// pages/(SuperAdmin)Profile/index.jsx (/DashboardSuper/*)
const SUPER_SHELL_PAGES = ['ConfigurationManagement', 'AdminAccountCreation', 'Settings'];

// React Router route matching is case-insensitive; compare the same way.
const shellHasPage = (pages, page) =>
  pages.some((p) => p.toLowerCase() === String(page || '').toLowerCase());

// ── View state helpers ──────────────────────────────────────────────────────

/** The persisted view selection ('user' | 'manager' | 'admin' | ''). */
export function getStoredViewType() {
  try {
    return (
      localStorage.getItem('managerViewType') ||
      localStorage.getItem('viewType') ||
      ''
    ).toLowerCase();
  } catch (e) {
    return '';
  }
}

/** Same admin-level heuristic used by every shell's hasAdminLevel(). */
export function hasStoredAdminLevel() {
  try {
    const raw = localStorage.getItem('adminLevel');
    if (!raw) return false;
    const adminLevel = JSON.parse(raw);
    return Boolean(
      adminLevel &&
      (adminLevel.adminLevelId || adminLevel.admin_level_id ||
        (Array.isArray(adminLevel.modules) && adminLevel.modules.length > 0))
    );
  } catch (e) {
    return false;
  }
}

/**
 * The view that is actually on screen. The URL prefix is the source of truth;
 * only the dual-mode shells (/DashboardManager for admin-level managers,
 * /userprofile for admin-level users) need the stored viewType to
 * disambiguate.
 */
export function getCurrentView(pathname, role) {
  const p = String(pathname || '').toLowerCase();
  if (p.startsWith('/dashboardsuper')) return 'super';
  if (p.startsWith('/dashboardmanager')) {
    return getStoredViewType() === 'admin' ? 'admin' : 'manager';
  }
  if (p.startsWith('/userprofileadmin') || p.startsWith('/userprofilemanager')) return 'user';
  if (p.startsWith('/userprofile')) {
    return role === 'user' && getStoredViewType() === 'admin' ? 'admin' : 'user';
  }
  if (p.startsWith('/dashboard')) return 'admin';
  return getStoredViewType() || (role === 'admin' ? 'admin' : role === 'manager' ? 'manager' : 'user');
}

// ── Admin-level permission pre-check ────────────────────────────────────────
// Only consulted when an admin-level manager/user is being routed into an
// Admin View page. Mirrors AuthContext.getPageState WITHOUT its "not in admin
// view" bypass (we are about to switch INTO admin view, so the bypass would
// evaluate against the wrong view). PageGuard remains the enforcement of
// record — this just avoids switching views into a page that will bounce.
function adminPageAllowed(page, permissions) {
  if (!permissions) return true;
  if (permissions.allModulesAllowed) return true;
  const moduleKey = pageToModuleMap[page];
  if (!moduleKey) return true; // dashboard & unmapped pages are always open
  const norm = (v) => String(v || '').trim().toLowerCase();
  const pagesMap = permissions.pages;
  if (pagesMap && typeof pagesMap === 'object' && pagesMap[norm(moduleKey)]) {
    return pagesMap[norm(moduleKey)] === 'VISIBLE_ACCESSIBLE';
  }
  return (permissions.modules || []).map(norm).includes(norm(moduleKey));
}

// ── Resolution ──────────────────────────────────────────────────────────────

const buildSearch = (query) => {
  if (!query) return '';
  const entries = Object.entries(query).filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );
  if (!entries.length) return '';
  return `?${new URLSearchParams(Object.fromEntries(entries)).toString()}`;
};

/**
 * Candidate (view, shell) pairs for this account, ordered so the CURRENT
 * view wins whenever it has a valid destination — the view only switches
 * when the notification's page doesn't exist in the active view.
 */
function candidateViews(role, currentView) {
  if (role === 'superadmin') {
    return [{ key: 'super', prefix: '/DashboardSuper', pages: SUPER_SHELL_PAGES, switchTo: null }];
  }

  if (role === 'admin') {
    const adminC = { key: 'admin', prefix: '/dashboard', pages: ADMIN_SHELL_PAGES, switchTo: 'admin' };
    const userC = { key: 'employee', prefix: '/userprofileadmin', pages: USER_VIEW_PAGES, switchTo: 'user' };
    return currentView === 'user' ? [userC, adminC] : [adminC, userC];
  }

  if (role === 'manager') {
    // Manager View and (admin-level) Admin View share the /DashboardManager
    // shell — landing there never requires flipping an active admin view.
    const shellView = currentView === 'admin' ? 'admin' : 'manager';
    const mgrC = { key: 'manager', prefix: '/DashboardManager', pages: MANAGER_SHELL_PAGES, switchTo: shellView };
    const mgrAdminC = { key: 'admin', prefix: '/DashboardManager', pages: MANAGER_SHELL_PAGES, switchTo: shellView };
    const userC = { key: 'employee', prefix: '/userprofilemanager', pages: USER_VIEW_PAGES, switchTo: 'user' };
    return currentView === 'user' ? [userC, mgrC, mgrAdminC] : [mgrC, mgrAdminC, userC];
  }

  // role 'user'
  const userC = { key: 'employee', prefix: '/userprofile', pages: USER_VIEW_PAGES, switchTo: 'user' };
  if (!hasStoredAdminLevel()) return [userC];
  const adminC = { key: 'admin', prefix: '/userprofile', pages: USER_ADMIN_ROUTES_PAGES, switchTo: 'admin', gated: true };
  return currentView === 'admin' ? [adminC, userC] : [userC, adminC];
}

/**
 * Resolve where a notification click should land for this viewer.
 *
 * @param {object} notification The (processed) notification object.
 * @param {object} ctx { role, permissions, pathname, selfCode }
 *   role        account role: 'admin' | 'manager' | 'user' | 'superadmin'
 *   permissions AuthContext permissions (admin-level payload), may be null
 *   pathname    current location.pathname
 *   selfCode    the viewer's own infinityCode (dual-audience disambiguation)
 * @returns {{ path: string, view: string|null, page: string }|null}
 *   view is the viewType to activate ('user'|'manager'|'admin'), or null when
 *   no switch applies (superadmin). Returns null when the notification has no
 *   valid destination for this account (click just marks as read).
 */
export function resolveNotificationNavigation(notification, { role, permissions, pathname, selfCode }) {
  const normalizedRole = String(role || 'user').toLowerCase();
  const { pages, query } = getNotificationTarget(notification, { selfCode });
  if (!pages || Object.keys(pages).length === 0) return null;

  const currentView = getCurrentView(pathname, normalizedRole);

  for (const candidate of candidateViews(normalizedRole, currentView)) {
    const page = pages[candidate.key];
    if (!page) continue;
    if (!shellHasPage(candidate.pages, page)) continue;
    if (candidate.gated && !adminPageAllowed(page, permissions)) continue;
    return {
      path: `${candidate.prefix}/${page}${buildSearch(query)}`,
      view: candidate.switchTo,
      page,
    };
  }
  return null;
}

/**
 * Synchronize the app's active view BEFORE navigating. Writes both storage
 * keys (different shells read different ones) and fires the same event the
 * ViewTypeSwitcher fires, so every mounted switcher and the dual-mode shells
 * (User_Account viewMode, attendance useViewType, ...) update immediately.
 * New shells mounted by the subsequent navigation read the fresh storage.
 */
export function applyViewSwitch(view) {
  if (!view) return;
  try {
    const changed =
      localStorage.getItem('viewType') !== view ||
      localStorage.getItem('managerViewType') !== view;
    if (!changed) return;
    localStorage.setItem('viewType', view);
    localStorage.setItem('managerViewType', view);
    window.dispatchEvent(new Event('managerViewTypeChanged'));
  } catch (e) {
    console.warn('Failed to synchronize view type before notification navigation', e);
  }
}
